import React, { FC } from "react";
import { defineMessages, useIntl } from "react-intl";
import { GridCell, GridRow } from "@natera/material/lib/layout";
import { Button, Form, FormField, Textfield } from "@natera/form";
import { useErrorController } from "@natera/platform/lib/hooks";
import { ValidationError } from "@natera/platform/lib/service/httpError";
import * as R from "ramda";
import { OrderContext, Profile } from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { getTestNameForHeap } from "@app/utils";

import "./signatureForm.scss";

const messages = defineMessages({
  signatureFormButtonNext: {
    id: "signatureFormButtonNext",
    defaultMessage: "Submit signature",
  },
  signatureFormButtonBack: {
    id: "signatureFormButtonBack",
    defaultMessage: "Back",
  },
  signatureFormFirstName: {
    id: "signatureFormFirstName",
    defaultMessage: "First Name",
  },
  signatureFormLastName: {
    id: "signatureFormLastName",
    defaultMessage: "Last Name",
  },
  validationErrorFirstNameRequired: {
    id: "validationErrorFirstNameRequired",
    defaultMessage: "Please enter your first name",
  },
  validationErrorLastNameRequired: {
    id: "validationErrorLastNameRequired",
    defaultMessage: "Please enter your last name",
  },
});

const firstName = "firstName";
const lastName = "lastName";

export type SignatureFormType = {
  firstName?: string;
  lastName?: string;
};

interface SignatureFormProps {
  profile?: Profile;
  initValues?: SignatureFormType;
  onSubmit: (formData: SignatureFormType) => void;
  loading?: boolean;
  goBack: () => void;
  isPatientSignature?: boolean;
}

const SignatureForm: FC<SignatureFormProps> = ({
  initValues,
  onSubmit,
  loading,
  goBack,
  profile,
  isPatientSignature,
}) => {
  const intl = useIntl();
  const { clearErrors, getValidationError, setValidationErrors } =
    useErrorController();

  const { orderData } = React.useContext(OrderContext);
  const orderUidPublic = orderData?.orderUidPublic;
  const limsId = orderData?.clinic?.limsId;

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    clearErrors();

    const formData = new FormData(event.currentTarget);

    const firstNameValue = (formData.get(firstName) as string | null)?.trim();
    const lastNameValue = (formData.get(lastName) as string | null)?.trim();

    const validationErrors: ValidationError[] = [];
    if (!firstNameValue) {
      validationErrors.push({
        name: firstName,
        message: intl.formatMessage(messages.validationErrorFirstNameRequired),
      });
    }
    if (!lastNameValue) {
      validationErrors.push({
        name: lastName,
        message: intl.formatMessage(messages.validationErrorLastNameRequired),
      });
    }

    if (!R.isEmpty(validationErrors)) {
      setValidationErrors(validationErrors);
      return;
    }

    const signatureInfo = {
      firstName: firstNameValue,
      lastName: lastNameValue,
    };
    onSubmit(signatureInfo);
    if (!isPatientSignature) {
      heap.track(HEAP_EVENTS.upp_simpleorder_partnerconsentyes, {
        tests_ordered: getTestNameForHeap(orderData),
        lims_clinic_id: limsId,
        public_order_uid: orderUidPublic,
      });
    }
  };

  return (
    <Form
      noValidate
      className="signature-form"
      data-testid="signatureForm"
      onSubmit={handleSubmit}
      autoComplete="off"
      buttons={
        <>
          <Button type="submit" unelevated loading={loading}>
            {!loading && intl.formatMessage(messages.signatureFormButtonNext)}
          </Button>
          <Button onClick={goBack} outlined disabled={loading}>
            {intl.formatMessage(messages.signatureFormButtonBack)}
          </Button>
        </>
      }
    >
      <GridRow>
        <GridCell span={12}>
          <FormField
            title={intl.formatMessage(messages.signatureFormFirstName)}
            label={intl.formatMessage(messages.signatureFormFirstName)}
            aria-label={intl.formatMessage(messages.signatureFormFirstName)}
            error={getValidationError(firstName)}
            required
            htmlFor={firstName}
            disabled={loading}
          >
            <Textfield
              id={firstName}
              name={firstName}
              type="text"
              placeholder={profile?.firstName}
              defaultValue={initValues?.firstName}
              outline
              required
              autoComplete="given-name"
            />
          </FormField>
        </GridCell>
        <GridCell span={12}>
          <FormField
            title={intl.formatMessage(messages.signatureFormLastName)}
            label={intl.formatMessage(messages.signatureFormLastName)}
            aria-label={intl.formatMessage(messages.signatureFormLastName)}
            error={getValidationError(lastName)}
            required
            htmlFor={lastName}
            disabled={loading}
          >
            <Textfield
              id={lastName}
              name={lastName}
              placeholder={profile?.lastName}
              defaultValue={initValues?.lastName}
              outline
              required
              autoComplete="family-name"
            />
          </FormField>
        </GridCell>
      </GridRow>
    </Form>
  );
};

export default SignatureForm;
